import { useEffect } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { FAQS } from "@/data/content";

// FAQ section component. Accepts:
//   items     – array of { q, a } (defaults to general FAQS)
//   eyebrow   – kicker text above the heading
//   title     – section heading
//   intro     – short intro paragraph
//   schemaId  – unique id for the JSON-LD <script>, so multiple FAQ blocks
//               on the same site/route don't collide. Required when used
//               with structured data.
export default function FAQSection({
  items = FAQS,
  eyebrow = "FAQs",
  title = "Common questions",
  intro = "Practical, plain answers to the questions individuals and organisations most often ask us.",
  schemaId = "faq-schema",
}) {
  // Inject / refresh FAQPage JSON-LD for SEO. We strip HTML tags from the
  // answer because schema.org expects plain text.
  useEffect(() => {
    if (!items?.length) return;
    const stripHtml = (s) => s.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
    const data = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: items.map((it) => ({
        "@type": "Question",
        name: stripHtml(it.q),
        acceptedAnswer: { "@type": "Answer", text: stripHtml(it.a) },
      })),
    };
    let el = document.getElementById(schemaId);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = schemaId;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
    return () => {
      const found = document.getElementById(schemaId);
      if (found) found.remove();
    };
  }, [items, schemaId]);

  return (
    <section className="py-20 md:py-28 bg-white" data-testid="faq-section">
      <div className="container-rail grid gap-12 lg:grid-cols-[1fr_2fr]">
        <div>
          <p className="label-eyebrow text-safety">{eyebrow}</p>
          <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">{title}</h2>
          <p className="mt-4 text-sm text-slate-600 leading-relaxed max-w-sm">{intro}</p>
        </div>
        <Accordion type="single" collapsible className="w-full border-t border-slate-200">
          {items.map((it, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-b border-slate-200">
              <AccordionTrigger
                className="py-5 text-left font-display font-semibold text-navy text-base md:text-lg hover:no-underline"
                data-testid={`faq-trigger-${i}`}
              >
                {it.q}
              </AccordionTrigger>
              <AccordionContent
                className="text-slate-600 text-[15px] leading-relaxed pb-6 [&_a]:text-navy [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:text-safety"
                data-testid={`faq-content-${i}`}
              >
                <span dangerouslySetInnerHTML={{ __html: it.a }} />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
